import LegalPageLayout from '../../components/LegalPageLayout'

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="March 1, 2026">
      <div className="space-y-6">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
          <p className="text-sm text-gray-700">
            <strong>PLACEHOLDER DOCUMENT:</strong> This document contains placeholder legal text. 
            You must have this reviewed and customized by a qualified legal professional before going live. 
            This is not legal advice.
          </p>
        </div>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
          <p className="text-gray-700 mb-4">
            LoanLoop ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains 
            how we collect, use, disclose, and safeguard your information when you use our Website.
          </p>
          <p className="text-gray-700">
            This policy complies with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth).
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
          
          <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">2.1 Information You Provide</h3>
          <p className="text-gray-700 mb-4">
            When you use our comparison tools, we may collect:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Loan amount and property value</li>
            <li>Income and employment information</li>
            <li>Contact information (if you request to be contacted)</li>
            <li>Preferences and search criteria</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">2.2 Automatically Collected Information</h3>
          <p className="text-gray-700 mb-4">
            We automatically collect certain information when you visit our Website:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Browser type and version</li>
            <li>Device information</li>
            <li>IP address</li>
            <li>Pages viewed and time spent</li>
            <li>Referring website</li>
            <li>Usage patterns and preferences</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">2.3 Cookies and Tracking</h3>
          <p className="text-gray-700">
            We use cookies and similar technologies to enhance your experience, analyze usage, and remember your preferences. 
            You can control cookie settings through your browser.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
          <p className="text-gray-700 mb-4">
            We use your information to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Provide comparison services and display relevant loan products</li>
            <li>Improve and personalize your experience</li>
            <li>Analyze Website usage and performance</li>
            <li>Respond to your inquiries</li>
            <li>Send updates about our services (if you opt in)</li>
            <li>Comply with legal obligations</li>
            <li>Prevent fraud and ensure Website security</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
          
          <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">4.1 When We Share Information</h3>
          <p className="text-gray-700 mb-4">
            We may share your information in the following circumstances:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li><strong>With Your Consent:</strong> When you explicitly request to be connected with lenders</li>
            <li><strong>Service Providers:</strong> With third parties who help us operate our Website (hosting, analytics, etc.)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our legal rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">4.2 What We Don't Share</h3>
          <p className="text-gray-700">
            We do not sell your personal information to third parties for their marketing purposes.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Storage and Security</h2>
          <p className="text-gray-700 mb-4">
            We implement reasonable security measures to protect your information from unauthorized access, 
            disclosure, alteration, or destruction. However, no internet transmission is completely secure, 
            and we cannot guarantee absolute security.
          </p>
          <p className="text-gray-700">
            Your data may be stored on servers located in Australia or overseas. We ensure that any overseas 
            data transfers comply with Australian privacy laws.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Privacy Rights</h2>
          <p className="text-gray-700 mb-4">
            Under Australian privacy law, you have the right to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your information (subject to legal obligations)</li>
            <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
            <li><strong>Complain:</strong> Lodge a complaint about our privacy practices</li>
          </ul>
          <p className="text-gray-700 mt-4">
            To exercise these rights, contact us at: privacy@loanloop.com.au
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Social Sign-In (Facebook, Google, Apple, Microsoft)</h2>
          <p className="text-gray-700 mb-4">
            You may choose to sign in to LoanLoop using a third-party account (Facebook, Google, Apple, or Microsoft).
            When you do, we receive the following information from that provider:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li><strong>Name</strong> — to pre-fill your profile</li>
            <li><strong>Email address</strong> — as your account identifier</li>
            <li><strong>Provider user ID</strong> — to link your account securely</li>
          </ul>
          <p className="text-gray-700 mt-4">
            We do not receive your social media password, friends list, posts, or any other data beyond what is listed above.
            This information is used solely to create and manage your LoanLoop account.
          </p>
          <p className="text-gray-700 mt-4">
            You can disconnect your social sign-in or request deletion of your account data at any time by contacting
            us at <strong>privacy@loanloop.com.au</strong>.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
          <p className="text-gray-700">
            Our Website is not intended for individuals under 18 years of age. We do not knowingly collect 
            personal information from children. If you believe we have inadvertently collected such information, 
            please contact us immediately.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Third-Party Links</h2>
          <p className="text-gray-700">
            Our Website may contain links to third-party websites, including lender websites. We are not 
            responsible for the privacy practices of these external sites. We encourage you to review their 
            privacy policies before providing any information.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Updates to This Policy</h2>
          <p className="text-gray-700">
            We may update this Privacy Policy from time to time. Changes will be posted on this page with 
            an updated "Last Updated" date. Continued use of the Website after changes constitutes acceptance 
            of the updated policy.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Cookie Policy</h2>
          <p className="text-gray-700 mb-4">
            We use the following types of cookies:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li><strong>Essential Cookies:</strong> Required for Website functionality</li>
            <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our Website</li>
            <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
          </ul>
          <p className="text-gray-700 mt-4">
            You can manage cookie preferences through your browser settings. Note that disabling cookies 
            may affect Website functionality.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
          <p className="text-gray-700 mb-4">
            For privacy-related questions, concerns, or to exercise your rights:
          </p>
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <p className="text-gray-700"><strong>Email:</strong> privacy@loanloop.com.au</p>
            <p className="text-gray-700"><strong>Address:</strong> [Your Business Address]</p>
            <p className="text-gray-700"><strong>Phone:</strong> [Your Phone Number]</p>
          </div>
          <p className="text-gray-700 mt-4">
            If you are not satisfied with our response, you may lodge a complaint with the Office of the 
            Australian Information Commissioner (OAIC) at www.oaic.gov.au.
          </p>
        </section>

        <div className="mt-8 p-6 bg-red-50 border-l-4 border-red-400">
          <p className="text-sm text-gray-700">
            <strong>LEGAL REVIEW REQUIRED:</strong> This document is a placeholder template only. 
            It must be reviewed, customized, and approved by a qualified legal professional before publication. 
            Ensure compliance with the Privacy Act 1988 (Cth) and Australian Privacy Principles.
          </p>
        </div>
      </div>
    </LegalPageLayout>
  )
}
